import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, deny } from '../types';
import type { LotStatus } from './lot';

export type LotReservationFacts = {
  /** The reserved lot's status — only an OPEN lot takes a new reservation. */
  lotStatus: LotStatus;
  /**
   * The reservation already deducted its units from the lot (a production run input), so
   * releasing it would not return them. `false` for a reservation not yet made.
   */
  hasInventoryImpact: boolean;
};

/**
 * A lot's reservations: `add` reserves units of the lot, `delete` releases a reservation.
 * There is no reservation update door, so the policy has no fields and no ops.
 */
const decideEntity = (facts: LotReservationFacts, action: Action<never, never>): Decision => {
  switch (action.type) {
    case 'view':
    case 'edit':
    case 'perform':
      return allow();
    case 'add':
      return facts.lotStatus === 'OPEN' ? allow() : deny('LOT_RESERVE_LOCKED', 'Only Open lots can be reserved.');
    case 'delete':
      return facts.hasInventoryImpact
        ? deny('LOT_RESERVATION_RELEASE_LOCKED', "This reservation affects inventory and can't be released here.")
        : allow();
  }
};

export const lotReservationPolicy: Policy<LotReservationFacts, never, never> = definePolicy<
  LotReservationFacts,
  never,
  never
>({ kind: 'lot reservation', fields: [], ops: [], requires: {}, entity: decideEntity });
